/**
 * Der Schriftwechsel — im Verlauf des Projekts, im Kundenkonto und in der
 * Sammelstelle „Post zu prüfen".
 *
 * Die Mails holt der Postlauf auf dem Server von selbst und hängt sie mit einem
 * Projekt*vorschlag* an. Hier wird der Vorschlag bestätigt, das Gespräch
 * umgehängt oder ausgeblendet — immer das ganze Gespräch: eine Bestätigung gilt
 * für den Faden, und weitere Antworten darin kommen danach bestätigt an.
 *
 * Mailtexte sind Fremdtexte. Sie werden als Text gezeigt (kein HTML), nichts
 * daraus wird ausgeführt. Anhänge liegen nur im Postfach; der Server holt sie
 * beim Öffnen von dort.
 */
import { useEffect, useMemo, useState } from "react";
import { fehlerText } from "@vimaana/ui";
import { Link } from "react-router-dom";

import { useProjektAkte, useProjekte } from "../../hooks/usePlanung";
import {
  anhangUrl,
  usePostAusblenden,
  usePostBestaetigen,
  usePostUmhaengen,
  usePostZuPruefen,
  useNeueAdresse,
  useNeueAdressen,
  useProjektAdressen,
  useProjektPost,
  useProjektZeitraum,
  useProjektZeitraumSetzen,
  type PostAbsender,
  type PostAntwort,
  type PostFaden,
  type PostNachricht,
  type PostProjektwahl,
} from "../../hooks/usePost";
import type { ProjektVerlaufEintrag } from "../../lib/types";
import Combobox, { type ComboboxEintrag } from "../Combobox";
import DokumentLightbox, { type Dokument } from "../DokumentLightbox";
import { MAIL_EREIGNIS } from "../../lib/sprung";
import PostSchreiben, { type SchreibZiel } from "./PostSchreiben";
import "../../styles/post.css";

const SPEICHER = "kontor.postZuPruefen.offen";

function zeit(iso: string, mitUhr = true): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", mitUhr
    ? { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/** `"Marianne Beispiel" <m@x.de>` → `Marianne Beispiel`; nur Adresse → die Adresse. */
function nurName(roh: string): string {
  const m = /^\s*"?([^"<]*?)"?\s*<[^>]+>\s*$/.exec(roh || "");
  return (m && m[1].trim()) || (roh || "").replace(/[<>]/g, "").trim();
}

function adresseAus(roh: string): string {
  const m = /<([^<>\s]+@[^<>\s]+)>/.exec(roh || "") || /([^\s<>,;"]+@[^\s<>,;"]+)/.exec(roh || "");
  return m ? m[1].toLowerCase() : "";
}

function initialen(name: string): string {
  const teile = name.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return ((teile[0]?.[0] || "?") + (teile[1]?.[0] || "")).toUpperCase();
}

function groesse(bytes: number): string {
  if (!bytes) return "";
  return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const ZITAT_BEGINN = /^(>|Am .{5,90} schrieb|On .{5,90} wrote|-{2,}\s*(Ursprüngliche|Original|Weitergeleitete)|Von: .+|From: .+|_{10,})/;

/**
 * Trennt das Geschriebene vom Mitgeschleppten. Fast jede Antwort hängt den
 * ganzen bisherigen Wechsel an — im Gespräch steht der aber schon darüber.
 * Besteht die Mail praktisch nur aus Zitat (eine Weiterleitung), bleibt alles sichtbar.
 */
function teileText(text: string): [string, string] {
  const zeilen = (text || "").split("\n");
  const i = zeilen.findIndex((z) => ZITAT_BEGINN.test(z.trim()));
  if (i < 0) return [text, ""];
  const sichtbar = zeilen.slice(0, i).join("\n").trim();
  if (sichtbar.length < 20) return [text, ""];
  return [sichtbar, zeilen.slice(i).join("\n").trim()];
}

function Nachricht({ n, zeigeAusgeblendete }: { n: PostNachricht; zeigeAusgeblendete: boolean }) {
  const [details, setDetails] = useState(false);
  const [zitat, setZitat] = useState(false);
  const [vorschau, setVorschau] = useState<Dokument>(null);
  const ausblenden = usePostAusblenden();
  const [sichtbar, rest] = useMemo(() => teileText(n.text), [n.text]);
  if (n.ausgeblendet && !zeigeAusgeblendete) return null;
  const name = n.richtung === "out" ? nurName(n.kopf?.von || n.absender) || "Wir" : nurName(n.absender) || "Eingang";

  return (
    <li id={`mail-${n.id}`} className={"vg-mail ist-" + n.richtung + (n.ausgeblendet ? " ist-ausgeblendet" : "")}>
      <div className="vg-mail-kopf">
        <span className="vg-mail-bild" aria-hidden="true">{initialen(name)}</span>
        <span className="vg-mail-wer">
          <strong>{name}</strong>
          <small>{zeit(n.zeitpunkt)}{n.ausgeblendet ? " · ausgeblendet" : ""}</small>
        </span>
        <button type="button" className="vg-post-link" aria-expanded={details} onClick={() => setDetails((v) => !v)}>
          {details ? "Details zu" : "Details"}
        </button>
      </div>

      {details && (
        <dl className="vg-mail-details">
          {n.kopf?.von && (<><dt>Von</dt><dd>{n.kopf.von}</dd></>)}
          {n.kopf?.an && (<><dt>An</dt><dd>{n.kopf.an}</dd></>)}
          {n.kopf?.cc && (<><dt>CC</dt><dd>{n.kopf.cc}</dd></>)}
          {n.betreff && (<><dt>Betreff</dt><dd>{n.betreff}</dd></>)}
          {n.postfach && (<><dt>Postfach</dt><dd>{n.postfach}</dd></>)}
          <dt />
          <dd>
            <button
              type="button"
              className="vg-post-link"
              disabled={ausblenden.isPending}
              onClick={() => ausblenden.mutate({ messageId: n.id, gespraech: false, ausblenden: !n.ausgeblendet })}
            >
              {n.ausgeblendet ? "diese Nachricht wieder zeigen" : "nur diese Nachricht ausblenden"}
            </button>
          </dd>
        </dl>
      )}

      <div className="vg-mail-text">{sichtbar || "(kein Text)"}</div>
      {rest && (
        <>
          <button type="button" className="vg-mail-zitat-knopf" aria-expanded={zitat} onClick={() => setZitat((v) => !v)}>
            {zitat ? "frühere Nachrichten verbergen" : "··· frühere Nachrichten"}
          </button>
          {zitat && <div className="vg-mail-text ist-zitat">{rest}</div>}
        </>
      )}

      {n.anhaenge.length > 0 && (
        <ul className="vg-mail-anhaenge" aria-label="Anhänge">
          {n.anhaenge.map((a) => (
            <li key={a.nr}>
              {a.mime === "application/pdf" || (a.mime.startsWith("image/") && !a.mime.includes("svg")) ? (
                /* PDF und Bilder im selben Fenster wie alle Dateien in Kontor — kein neuer Tab. */
                <button
                  type="button" className="vg-mail-anhang"
                  onClick={() => setVorschau({
                    titel: a.name, unterzeile: `${name} · ${zeit(n.zeitpunkt)}`, url: anhangUrl(n.id, a.nr),
                    bild: a.mime.startsWith("image/"),
                    seitenZahlUrl: `${anhangUrl(n.id, a.nr)}/seiten`, seiteUrl: (i) => `${anhangUrl(n.id, a.nr)}/seite/${i}`,
                  })}
                >
                  <span aria-hidden="true">{a.mime === "application/pdf" ? "📄" : "🖼"}</span>
                  <span className="vg-mail-anhang-name">{a.name}</span>
                  <small>{groesse(a.bytes)}</small>
                </button>
              ) : (
                <a href={anhangUrl(n.id, a.nr)} target="_blank" rel="noopener noreferrer" className="vg-mail-anhang">
                  <span aria-hidden="true">📎</span>
                  <span className="vg-mail-anhang-name">{a.name}</span>
                  <small>{groesse(a.bytes)}</small>
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
      <DokumentLightbox dok={vorschau} onClose={() => setVorschau(null)} />
    </li>
  );
}

function ProjektWahl({
  faden, auswahl, onFertig,
}: { faden: PostFaden; auswahl: PostProjektwahl[]; onFertig: () => void }) {
  const [ziel, setZiel] = useState(faden.projekt_id);
  const haengen = usePostUmhaengen();
  // Gesucht wird über ALLE Projekte (Benjamin 28.09.2026): eine Lieferantenmail
  // gehört oft zu einem ganz anderen Kunden als dem des Projekts, an dem sie gerade
  // hängt. Die Projekte des Kunden stehen vorn, weil sie die naheliegenden sind.
  const alle = useProjekte(true);
  const eintraege = useMemo<ComboboxEintrag[]>(() => {
    const vorn = new Set(auswahl.map((p) => p.id));
    return [
      ...auswahl.map((p) => ({ id: p.id, label: p.titel || "Projekt", zusatz: [faden.kunde_name, p.status].filter(Boolean).join(" · ") })),
      ...(alle.data || [])
        .filter((p) => !vorn.has(p.id))
        .map((p) => ({ id: p.id, label: p.title || "Projekt", zusatz: [p.kunde_name, p.status].filter(Boolean).join(" · ") })),
    ];
  }, [auswahl, alle.data, faden.kunde_name]);
  // Ohne Projekt bleibt eine Kundenmail am Kunden, eine Lieferantenmail am Lieferanten.
  const leerLabel = faden.kunde_id ? "— zu keinem Projekt (bleibt am Kunden)" : "— zu keinem Projekt (bleibt beim Lieferanten)";
  const fehler = haengen.error as Error | null;
  return (
    <>
      <div className="vg-post-wahl-suche">
        <Combobox
          wert={ziel}
          eintraege={eintraege}
          onWaehlen={setZiel}
          leerLabel={leerLabel}
          platzhalter="Projekt oder Kunde tippen …"
          ariaLabel="Zielprojekt"
        />
      </div>
      <button
        type="button"
        className="vg-btn vg-btn-primary"
        disabled={haengen.isPending || (ziel === faden.projekt_id && !faden.vorschlag)}
        onClick={() => haengen.mutate({ messageId: faden.anker_id, projektId: ziel }, { onSuccess: onFertig })}
      >
        Umhängen
      </button>
      <button type="button" className="vg-btn vg-btn-ghost" disabled={haengen.isPending} onClick={onFertig}>
        Abbrechen
      </button>
      {fehler && <p className="vg-post-fehler">{fehlerText(fehler)}</p>}
    </>
  );
}

export function PostFadenKarte({
  faden, auswahl, mitZiel, zeigeAusgeblendete = false, absenderFilter = "", nurLesen = false, anfrageId = "", anfangsOffen = false,
}: {
  /** Bei Leeds ist das Gespräch die Hauptsache — gleich aufgeklappt zeigen. */
  anfangsOffen?: boolean;
  /** Bei Leeds: die Antwort gehört an die Anfrage, nicht an ein Projekt. */
  anfrageId?: string;
  /** Bei Leeds: es gibt noch kein Projekt, also nichts zu bestätigen oder umzuhängen. */
  nurLesen?: boolean;
  faden: PostFaden;
  auswahl: PostProjektwahl[];
  /** Zeigt, an welchem Kunden/Projekt das Gespräch hängt (Sammelstelle, Kundenkonto). */
  mitZiel?: boolean;
  zeigeAusgeblendete?: boolean;
  absenderFilter?: string;
}) {
  const [offen, setOffen] = useState(anfangsOffen);
  // Sprung aus der KI-Spur oder der Kurzform zu einer Mail: das Gespräch aufklappen.
  useEffect(() => {
    const zeigen = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (faden.nachrichten.some((n) => n.id === id)) setOffen(true);
    };
    window.addEventListener(MAIL_EREIGNIS, zeigen);
    return () => window.removeEventListener(MAIL_EREIGNIS, zeigen);
  }, [faden.nachrichten]);
  const [umhaengen, setUmhaengen] = useState(false);
  const [antworten, setAntworten] = useState(false);
  const bestaetigen = usePostBestaetigen();
  const ausblenden = usePostAusblenden();
  const laeuft = bestaetigen.isPending || ausblenden.isPending;
  const fehler = (bestaetigen.error || ausblenden.error) as Error | null;

  const nachrichten = faden.nachrichten.filter(
    (n) => (!n.ausgeblendet || zeigeAusgeblendete) && (!absenderFilter || n.absender_schluessel === absenderFilter),
  );
  if (nachrichten.length === 0) return null;
  const neueste = nachrichten[0];
  const beteiligte = Object.values(faden.absender || {}).join(" ↔ ");
  // Geantwortet wird dem Gegenüber: Absender der letzten eingehenden Mail, sonst Empfänger der letzten eigenen.
  const letzteEin = faden.nachrichten.find((n) => n.richtung === "in");
  const gegenueber = letzteEin ? adresseAus(letzteEin.kopf?.von || "") : adresseAus(faden.nachrichten[0]?.kopf?.an || "");
  const fadenZiele: SchreibZiel[] = [];
  for (const n of faden.nachrichten) {
    const a = adresseAus(n.richtung === "in" ? n.kopf?.von || "" : n.kopf?.an || "");
    if (a && !a.endsWith("@vimaanagarten.de") && !fadenZiele.some((z) => z.email === a)) fadenZiele.push({ email: a, wer: n.richtung === "in" ? nurName(n.absender) || a : a });
  }
  const reBetreff = /^\s*(re|aw)\s*:/i.test(faden.betreff) ? faden.betreff : `Re: ${faden.betreff}`;

  return (
    <li className={"vg-post-faden" + (faden.vorschlag ? " ist-vorschlag" : "") + (faden.ausgeblendet ? " ist-ausgeblendet" : "")}>
      <button type="button" className="vg-post-kopf" onClick={() => setOffen((v) => !v)} aria-expanded={offen}>
        <span className="vg-post-chev" data-open={offen}>▸</span>
        <span className="vg-post-kopf-text">
          <span className="vg-post-betreff">{faden.betreff || "(ohne Betreff)"}</span>
          <span className="vg-post-meta">
            {beteiligte || nurName(neueste.absender)}
            {nachrichten.length > 1 ? ` · ${nachrichten.length} Nachrichten` : ""}
            {faden.anhaenge > 0 ? ` · 📎 ${faden.anhaenge}` : ""}
          </span>
        </span>
        <span className="vg-post-datum">{zeit(faden.letzte, false)}</span>
      </button>

      {mitZiel && (
        <p className="vg-post-ziel">
          {faden.kunde_name && <Link to={`/kunden?kunde=${faden.kunde_id}`}>{faden.kunde_name}</Link>}
          {faden.kunde_name && " → "}
          {faden.projekt_id
            ? <Link to={`/projekte/${faden.projekt_id}`}>{faden.projekt_titel || "Projekt"}</Link>
            : <em>kein Projekt</em>}
        </p>
      )}

      {nurLesen ? null : faden.ausgeblendet ? (
        <div className="vg-post-aktion">
          <span className="vg-post-marke ist-grau">ausgeblendet</span>
          <button
            type="button" className="vg-post-link" disabled={laeuft}
            onClick={() => ausblenden.mutate({ messageId: faden.anker_id, gespraech: true, ausblenden: false })}
          >
            wieder zeigen
          </button>
        </div>
      ) : faden.vorschlag ? (
        <div className="vg-post-aktion">
          <span className="vg-post-marke">Vorschlag{faden.grund ? ` · ${faden.grund}` : ""}</span>
          {!umhaengen ? (
            <>
              <button type="button" className="vg-btn vg-btn-primary" disabled={laeuft} onClick={() => bestaetigen.mutate(faden.anker_id)}>
                Stimmt
              </button>
              <button type="button" className="vg-btn" disabled={laeuft} onClick={() => setUmhaengen(true)}>
                Gehört woanders hin
              </button>
              <button
                type="button" className="vg-btn vg-btn-ghost" disabled={laeuft}
                onClick={() => ausblenden.mutate({ messageId: faden.anker_id, gespraech: true, ausblenden: true })}
              >
                Ausblenden
              </button>
            </>
          ) : (
            <ProjektWahl faden={faden} auswahl={auswahl} onFertig={() => setUmhaengen(false)} />
          )}
        </div>
      ) : (
        <div className="vg-post-aktion ist-leise">
          {faden.grund && <span className="vg-post-grund">{faden.grund}</span>}
          {!umhaengen ? (
            <>
              <button type="button" className="vg-post-link" onClick={() => setUmhaengen(true)}>umhängen</button>
              <button
                type="button" className="vg-post-link" disabled={laeuft}
                onClick={() => ausblenden.mutate({ messageId: faden.anker_id, gespraech: true, ausblenden: true })}
              >
                ausblenden
              </button>
            </>
          ) : (
            <ProjektWahl faden={faden} auswahl={auswahl} onFertig={() => setUmhaengen(false)} />
          )}
        </div>
      )}
      {fehler && <p className="vg-post-fehler">{fehlerText(fehler)}</p>}

      {offen && (
        <>
          {!faden.ausgeblendet && (
            <div className="vg-post-antworten">
              <button type="button" className="vg-btn" onClick={() => setAntworten(true)}>Antworten</button>
            </div>
          )}
          <ol className="vg-post-nachrichten">
            {nachrichten.map((n) => <Nachricht key={n.id} n={n} zeigeAusgeblendete={zeigeAusgeblendete} />)}
          </ol>
        </>
      )}
      {antworten && (
        <PostSchreiben
          onClose={() => setAntworten(false)}
          projektId={faden.projekt_id || undefined} kundeId={faden.kunde_id || undefined}
          anfrageId={anfrageId || undefined}
          ziele={fadenZiele} an={gegenueber} betreff={reBetreff} antwortAuf={faden.nachrichten[0]?.id}
          titel={faden.betreff}
        />
      )}
    </li>
  );
}

/** Absender-Filter und der Schalter für Ausgeblendetes — gleich im Projekt und beim Kunden. */
export function PostFilterLeiste({
  absender, wert, onWert, ausgeblendet, zeige, onZeige,
}: {
  absender: PostAbsender[]; wert: string; onWert: (s: string) => void;
  ausgeblendet: number; zeige: boolean; onZeige: (b: boolean) => void;
}) {
  if (absender.length < 2 && ausgeblendet === 0) return null;
  return (
    <div className="vg-verlauf-filter ist-unter" role="group" aria-label="Post nach Absender filtern">
      {absender.length >= 2 && (
        <>
          <span className="vg-verlauf-filter-titel">von</span>
          <button type="button" className={"vg-chip-knopf" + (wert === "" ? " is-active" : "")} aria-pressed={wert === ""} onClick={() => onWert("")}>
            allen
          </button>
          {absender.map((a) => (
            <button
              key={a.schluessel} type="button"
              className={"vg-chip-knopf" + (wert === a.schluessel ? " is-active" : "")}
              aria-pressed={wert === a.schluessel}
              onClick={() => onWert(a.schluessel)}
            >
              {a.name}
            </button>
          ))}
        </>
      )}
      {ausgeblendet > 0 && (
        <button type="button" className={"vg-chip-knopf ist-rechts" + (zeige ? " is-active" : "")} aria-pressed={zeige} onClick={() => onZeige(!zeige)}>
          {zeige ? "Ausgeblendete verbergen" : `${ausgeblendet} ausgeblendet`}
        </button>
      )}
    </div>
  );
}

/** Gefilterte Gespräche einer Post-Antwort — die eine Stelle, an der gefiltert wird. */
export function sichtbareFaeden(post: PostAntwort | undefined, absenderFilter: string, zeigeAusgeblendete: boolean): PostFaden[] {
  return (post?.faeden || []).filter((f) => {
    if (f.ausgeblendet && !zeigeAusgeblendete) return false;
    return f.nachrichten.some(
      (n) => (!n.ausgeblendet || zeigeAusgeblendete) && (!absenderFilter || n.absender_schluessel === absenderFilter),
    );
  });
}

type VerlaufArt = "" | "post" | "stand" | "dokument" | "termin" | "notiz" | "foto";
const VERLAUF_CHIPS: { id: VerlaufArt; label: string }[] = [
  { id: "", label: "Alles" },
  { id: "post", label: "Post" },
  { id: "stand", label: "Stand" },
  { id: "dokument", label: "Belege" },
  { id: "termin", label: "Termine" },
  { id: "notiz", label: "Notizen" },
  { id: "foto", label: "Fotos" },
];
const ART_VON_TYP: Record<string, VerlaufArt> = {
  dokument: "dokument", kosten: "dokument", termin: "termin", akte: "notiz", wissen: "notiz", aufnahme: "foto",
};
/** Ein Stand ist ein Akteneintrag wie eine Notiz — im Verlauf aber eine eigene Art. */
function artVon(e: ProjektVerlaufEintrag): VerlaufArt {
  if (e.typ === "akte" && e.untertyp === "stand") return "stand";
  return ART_VON_TYP[e.typ] || "notiz";
}

const UNTERMARKE: Record<string, string> = { stand: "Stand", naechster_schritt: "Schritt", entscheidung: "Entscheidung" };
const MARKE: Record<string, string> = {
  dokument: "Beleg", kosten: "Kosten", termin: "Termin", akte: "Notiz", wissen: "Wissen", aufnahme: "Fotos",
};

function tagDe(iso: string): string {
  const [j, m, t] = (iso || "").slice(0, 10).split("-");
  return j && m && t ? `${t}.${m}.${j.slice(2)}` : "";
}

/**
 * Der Zeitraum, nach dem Post diesem Projekt zugeordnet wird — sichtbar und von
 * Hand änderbar. Leer lassen heißt: automatisch (erstes Angebot … letzte Rechnung).
 * Nach dem Speichern holt der Server Post im neuen Zeitraum nach und ordnet
 * alle noch unbestätigten Gespräche des Kunden neu zu; Bestätigtes bleibt.
 */
function ZeitraumZeile({ projektId }: { projektId: string }) {
  const { data } = useProjektZeitraum(projektId);
  const setzen = useProjektZeitraumSetzen(projektId);
  const [offen, setOffen] = useState(false);
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  if (!data) return null;
  const oeffnen = () => {
    setVon(data.von_von_hand ? data.von : "");
    setBis(data.bis_von_hand ? data.bis : "");
    setzen.reset();
    setOffen(true);
  };
  const w = setzen.data;
  const fehler = setzen.error as Error | null;
  return (
    <div className="vg-zeitraum">
      <p className="vg-zeitraum-zeile">
        <span className="vg-verlauf-filter-titel">Zeitraum</span>
        <strong>{tagDe(data.von) || "offen"} – {tagDe(data.bis) || "offen"}</strong>
        <span className="vg-post-grund">
          {data.von_von_hand || data.bis_von_hand ? "von Hand gesetzt" : "automatisch aus Angeboten, Rechnungen und Terminen"}
        </span>
        {!offen && <button type="button" className="vg-post-link" onClick={oeffnen}>ändern</button>}
      </p>
      {offen && (
        <div className="vg-zeitraum-form">
          <label>
            von
            <input type="date" className="vg-stand-feld" value={von} onChange={(e) => setVon(e.target.value)} />
            <small>leer = automatisch{data.von_auto ? ` (${tagDe(data.von_auto)})` : ""}</small>
          </label>
          <label>
            bis
            <input type="date" className="vg-stand-feld" value={bis} onChange={(e) => setBis(e.target.value)} />
            <small>leer = automatisch{data.bis_auto ? ` (${tagDe(data.bis_auto)})` : ""}</small>
          </label>
          <div className="vg-stand-knoepfe">
            <button type="button" className="vg-btn vg-btn-primary" disabled={setzen.isPending} onClick={() => setzen.mutate({ von, bis })}>
              {setzen.isPending ? "Post wird geprüft …" : "Speichern und Post prüfen"}
            </button>
            <button type="button" className="vg-btn vg-btn-ghost" disabled={setzen.isPending} onClick={() => setOffen(false)}>
              Schließen
            </button>
          </div>
          {w && (
            <p className="vg-zeitraum-wirkung" role="status">
              {w.geholt > 0 ? `${w.geholt} Mails aus dem Postfach nachgeholt. ` : ""}
              {w.dazu === 0 && w.weg === 0
                ? "An der Zuordnung hat sich nichts geändert."
                : `${w.dazu} ${w.dazu === 1 ? "Mail kam" : "Mails kamen"} dazu, ${w.weg} ${w.weg === 1 ? "fiel" : "fielen"} heraus.`}
              {" "}Bestätigte Gespräche bleiben, wo sie sind.{w.hinweis ? ` ${w.hinweis}` : ""}
            </p>
          )}
          {fehler && <p className="vg-post-fehler">{fehlerText(fehler)}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * Der eine Verlauf des Projekts: Post, Belegereignisse, Termine, Notizen, Fotos —
 * nach Zeit sortiert, nach Art filterbar. Die Post kommt aus `connect_message`,
 * alles andere aus der Projektakte; keine der Quellen wird kopiert.
 */
/**
 * „Wer ist das?" — schreibt in einem Projektgespräch jemand von einer Adresse,
 * die beim Kunden nicht steht, fragt Kontor nach. Ein Klick macht daraus die
 * Zweitadresse derselben Person oder einen neuen Ansprechpartner; beides landet
 * beim Kunden, und künftige Mails von dort werden von selbst erkannt.
 */
function NeueAdressenFrage({ projektId }: { projektId: string }) {
  const frage = useNeueAdressen(projektId);
  const entscheid = useNeueAdresse(projektId);
  const [person, setPerson] = useState<{ email: string; name: string; rolle: string } | null>(null);
  const adressen = frage.data?.adressen || [];
  if (adressen.length === 0) return null;
  const kunde = frage.data?.kunde_name || "dem Kunden";
  return (
    <div className="vg-adressfrage" role="group" aria-label="Neue Adressen im Schriftwechsel">
      {adressen.map((a) => (
        <div className="vg-adressfrage-zeile" key={a.email}>
          <p>
            <strong>{a.email}</strong>{a.name ? ` (${a.name})` : ""} schreibt in diesem Projekt mit, steht aber nicht bei {kunde}.
            {a.betreff ? <small> Zuerst in „{a.betreff}“.</small> : null}
          </p>
          {person?.email === a.email ? (
            <div className="vg-adressfrage-form">
              <input className="vg-stand-feld" value={person.name} onChange={(e) => setPerson({ ...person, name: e.target.value })} placeholder="Name" aria-label="Name des Ansprechpartners" autoFocus />
              <input className="vg-stand-feld" value={person.rolle} onChange={(e) => setPerson({ ...person, rolle: e.target.value })} placeholder="Rolle, z. B. Bauleitung, Partnerin" aria-label="Rolle" />
              <button type="button" className="vg-btn vg-btn-primary" disabled={entscheid.isPending || !person.name.trim()}
                onClick={() => entscheid.mutate({ email: a.email, aktion: "ansprechpartner", name: person.name, rolle: person.rolle }, { onSuccess: () => setPerson(null) })}>
                Anlegen
              </button>
              <button type="button" className="vg-btn vg-btn-ghost" onClick={() => setPerson(null)}>Abbrechen</button>
            </div>
          ) : (
            <div className="vg-adressfrage-knoepfe">
              <button type="button" className="vg-btn vg-btn-primary" disabled={entscheid.isPending}
                onClick={() => entscheid.mutate({ email: a.email, aktion: "zweitadresse" })}>
                Zweitadresse von {kunde}
              </button>
              <button type="button" className="vg-btn" disabled={entscheid.isPending}
                onClick={() => setPerson({ email: a.email, name: a.name, rolle: "" })}>
                Neuer Ansprechpartner
              </button>
              <button type="button" className="vg-post-link" disabled={entscheid.isPending}
                onClick={() => entscheid.mutate({ email: a.email, aktion: "ignorieren" })}>
                nicht mehr fragen
              </button>
            </div>
          )}
        </div>
      ))}
      {entscheid.error && <p className="vg-post-fehler">{fehlerText(entscheid.error)}</p>}
    </div>
  );
}

export function ProjektVerlauf({ projektId }: { projektId: string }) {
  const akte = useProjektAkte(projektId);
  const post = useProjektPost(projektId);
  const [art, setArt] = useState<VerlaufArt>("");
  const [absender, setAbsender] = useState("");
  const [zeige, setZeige] = useState(false);
  const [schreiben, setSchreiben] = useState(false);
  const adressen = useProjektAdressen(projektId);

  const zeilen = useMemo(() => {
    const eintraege: ({ zeit: string } & ({ faden: PostFaden } | { eintrag: ProjektVerlaufEintrag }))[] = [];
    if (art === "" || art === "post") {
      for (const f of sichtbareFaeden(post.data, absender, zeige)) eintraege.push({ zeit: f.letzte, faden: f });
    }
    if (art !== "post") {
      for (const e of akte.data?.verlauf || []) {
        // Die Post steht als Gespräch im Verlauf — nicht noch einmal als Einzelzeile.
        if (e.typ === "kommunikation") continue;
        const eigene = artVon(e);
        if (art === "" || art === eigene) eintraege.push({ zeit: e.zeitpunkt, eintrag: e });
      }
    }
    return eintraege.sort((a, b) => (a.zeit < b.zeit ? 1 : -1));
  }, [akte.data, post.data, art, absender, zeige]);

  const zahl = (id: VerlaufArt): number | null => {
    if (id === "") return null;
    if (id === "post") return sichtbareFaeden(post.data, "", false).length;
    return (akte.data?.verlauf || []).filter((e) => e.typ !== "kommunikation" && artVon(e) === id).length;
  };

  if (akte.isLoading || post.isLoading) return <p className="vg-detail-section-hint">Lade Verlauf …</p>;

  return (
    <>
      <ZeitraumZeile projektId={projektId} />
      <NeueAdressenFrage projektId={projektId} />
      <div className="vg-verlauf-filter" role="group" aria-label="Verlauf filtern">
        {VERLAUF_CHIPS.map((c) => {
          const n = zahl(c.id);
          if (n === 0) return null;
          return (
            <button
              key={c.id || "alles"} type="button"
              className={"vg-chip-knopf" + (art === c.id ? " is-active" : "")}
              aria-pressed={art === c.id}
              onClick={() => setArt(c.id)}
            >
              {c.label}{n ? <span className="vg-chip-zahl">{n}</span> : null}
            </button>
          );
        })}
        <button type="button" className="vg-chip-knopf ist-rechts ist-tat" onClick={() => setSchreiben(true)}>
          ✎ Schreiben
        </button>
      </div>
      {schreiben && (
        <PostSchreiben
          onClose={() => setSchreiben(false)} projektId={projektId} kundeId={adressen.data?.kunde_id}
          ziele={(adressen.data?.ziele || []).map((z) => ({ email: z.email, wer: z.art === "lieferant" ? `${z.wer} (Lieferant)` : z.wer }))}
          an={adressen.data?.ziele.find((z) => z.art === "kunde")?.email || ""}
          titel="Nachricht zum Projekt"
        />
      )}
      {(art === "" || art === "post") && post.data && (
        <PostFilterLeiste
          absender={post.data.absender} wert={absender} onWert={setAbsender}
          ausgeblendet={post.data.ausgeblendet} zeige={zeige} onZeige={setZeige}
        />
      )}

      {zeilen.length === 0 && (
        <p className="vg-detail-section-hint" style={{ marginTop: 12 }}>
          {art === "post" || art === ""
            ? "Noch nichts. Mails von und an den Kunden holt Kontor alle 15 Minuten von selbst — Voraussetzung ist eine Mailadresse beim Kunden."
            : "In dieser Ansicht steht noch nichts."}
        </p>
      )}

      <ul className="vg-post-liste vg-verlauf-liste">
        {zeilen.map((z) =>
          "faden" in z ? (
            <PostFadenKarte
              key={"f-" + z.faden.schluessel} faden={z.faden}
              auswahl={post.data?.projekte_des_kunden || []}
              zeigeAusgeblendete={zeige} absenderFilter={absender}
            />
          ) : (
            <li key={`${z.eintrag.typ}-${z.eintrag.id}`} className={"vg-verlauf-zeile ist-" + artVon(z.eintrag)}>
              <span className="vg-verlauf-punkt" aria-hidden="true" />
              <span className="vg-verlauf-inhalt">
                <strong>{z.eintrag.titel}</strong>
                {z.eintrag.text && <span className="vg-verlauf-text">{z.eintrag.text}</span>}
              </span>
              <span className="vg-verlauf-meta">
                <span className="vg-verlauf-marke">{UNTERMARKE[z.eintrag.untertyp] || MARKE[z.eintrag.typ] || "Eintrag"}</span>
                {zeit(z.eintrag.zeitpunkt, false)}
              </span>
            </li>
          ),
        )}
      </ul>
    </>
  );
}

/** Kopfzeile der Sektion: wie viel Post, wie viel davon noch Vorschlag. */
export function useProjektVerlaufKopf(projektId: string): string {
  const { data } = useProjektPost(projektId);
  if (!data || data.nachrichten === 0) return "";
  const teile = [`${data.nachrichten} ${data.nachrichten === 1 ? "Mail" : "Mails"}`];
  if (data.vorschlaege > 0) teile.push(`${data.vorschlaege} zu bestätigen`);
  return teile.join(" · ");
}

/** Die Sammelstelle über der Projektliste: Gespräche, bei denen der Import nicht sicher sein kann. */
export default function PostZuPruefenLeiste() {
  const { data, isLoading } = usePostZuPruefen();
  const [offen, setOffen] = useState(() => {
    try { return localStorage.getItem(SPEICHER) === "1"; } catch { return false; }
  });
  const umschalten = () =>
    setOffen((vorher) => {
      const neu = !vorher;
      try { localStorage.setItem(SPEICHER, neu ? "1" : "0"); } catch { /* egal */ }
      return neu;
    });

  if (isLoading || !data || data.faeden.length === 0) return null;
  const anzahl = data.faeden.length;

  return (
    <section className={"vg-punkte" + (offen ? " is-offen" : "")} aria-label="Post zu prüfen">
      <button type="button" className="vg-punkte-kopf" onClick={umschalten} aria-expanded={offen}>
        <span className="vg-punkte-zahl">{anzahl}</span>
        <span className="vg-punkte-titel">
          {anzahl === 1 ? "Gespräch" : "Gespräche"} in der Post zu prüfen
        </span>
        {!offen && (
          <span className="vg-punkte-vorschau">
            {data.faeden.slice(0, 3).map((f) => (
              <em key={f.schluessel}>{f.kunde_name || f.betreff}</em>
            ))}
          </span>
        )}
        <span className="vg-punkte-pfeil" aria-hidden="true">{offen ? "▴" : "▾"}</span>
      </button>
      {offen && (
        <div className="vg-post-sammel">
          <p className="vg-detail-section-hint">
            Hier steht nur, was nicht eindeutig ist: der Kunde hat mehrere offene Projekte und im Text
            steht keine Belegnummer, oder die Mail kommt von einem Lieferanten. Geraten wird nicht.
          </p>
          <ul className="vg-post-liste">
            {data.faeden.map((f) => (
              <PostFadenKarte key={f.schluessel} faden={f} auswahl={f.projekte_des_kunden ?? []} mitZiel />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
